import React, { createContext, useContext, useState, useEffect } from 'react';
import TaskDialog from '../components/TaskDialog';
import { useAuth } from '../components/AuthContext';
import api from '../api';

const TaskDialogContext = createContext();

export const useTaskDialog = () => {
  const context = useContext(TaskDialogContext);
  if (!context) {
    throw new Error('useTaskDialog must be used within a TaskDialogProvider');
  }
  return context;
};

export const TaskDialogProvider = ({ children }) => {
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [currentObjectId, setCurrentObjectId] = useState(null);
  const [refreshCallbacks, setRefreshCallbacks] = useState({});
  const [userList, setUserList] = useState([]);
  const { user } = useAuth();

  // Load user list when provider mounts
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const res = await api.get('/users');
        setUserList(res.data);
      } catch (error) {
        console.log('Failed to load users for TaskDialogProvider:', error);
        setUserList([]);
      }
    };

    if (user) {
      loadUsers();
    }
  }, [user]);

  const openTaskDialog = (task = null, options = {}) => {
    console.log('🚀 Global openTaskDialog called with:', task?.title || 'new task');
    setEditingTask(task);
    setTaskDialogOpen(true);
    
    // Set objectId - either from existing task or from options
    setCurrentObjectId(task?.object?._id || options.objectId);
    
    // Store refresh callbacks for later use
    setRefreshCallbacks(options.refreshCallbacks || {});
  };

  const closeTaskDialog = () => {
    console.log('🚪 Global task dialog closing');
    setTaskDialogOpen(false);
    setEditingTask(null);
    setCurrentObjectId(null);
    setRefreshCallbacks({});
  };

  const handleTaskSave = async (taskData) => {
    console.log('💾 Global task save with callbacks:', Object.keys(refreshCallbacks));
    
    try {
      // Call all registered refresh callbacks
      const promises = [];
      
      if (refreshCallbacks.refreshTasks) {
        promises.push(refreshCallbacks.refreshTasks());
      }
      
      if (refreshCallbacks.refreshObject) {
        promises.push(refreshCallbacks.refreshObject());
      }
      
      if (refreshCallbacks.refreshData) {
        promises.push(refreshCallbacks.refreshData(taskData));
      }
      
      if (refreshCallbacks.reloadWelcomeData) {
        refreshCallbacks.reloadWelcomeData();
      }
      
      // Wait for all refresh operations to complete
      await Promise.all(promises);
      
      closeTaskDialog();
      
    } catch (error) {
      console.error('Error in task save callbacks:', error);
      // Still close dialog even if refresh fails
      closeTaskDialog();
    }
  };

  const contextValue = {
    openTaskDialog,
    closeTaskDialog,
    taskDialogOpen,
    editingTask
  };

  return (
    <TaskDialogContext.Provider value={contextValue}>
      {children}
      
      {/* Global Task Dialog */}
      <TaskDialog
        open={taskDialogOpen}
        onClose={closeTaskDialog}
        task={editingTask}
        objectId={currentObjectId}
        onTaskSave={handleTaskSave}
        userList={userList}
        currentUserId={user?.id}
      />
    </TaskDialogContext.Provider>
  );
};